import { useContext, useEffect, useRef, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import Input from "@mui/material/Input";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import HtmlEditor from "../../HtmlEditor/HtmlEditor";
import ButtonBase from "@mui/material/ButtonBase";
import { blobToData } from "../../../../helpers/data";
import { toast } from "react-toastify";
import AuthContext from "../../../../store/auth-contex";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../../Button/Button";

const generateBootstrapClasses = (columns) => {
    if (columns) {
        const columnClasses = [];
        Object.keys(columns).forEach((screenSize) => {
            const columnValue = columns[screenSize];
            if (columnValue) {
                columnClasses.push(`col-${screenSize}-${columnValue}`);
            }
        });
        return columnClasses.join(" ");
    }
    return "";
};

/**
 * Wrapper for the input element
 *
 * @param {JSX.Element} children
 * @param {string} label Field label
 * @param {string} error Error message
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 *
 * @return {JSX.Element}
 */

export const InputWrapper = ({ children = null, label, required, disabled, margin = "dense", error = null, fullWidth = true, styleFormControl, inputClasses }) => {
    return (
        <FormControl fullWidth={fullWidth} margin={margin} error={error !== null} sx={{ padding: "0 0.3rem", ...styleFormControl }} className={inputClasses}>
            <FormLabel required={required} disabled={disabled}>
                {label}
            </FormLabel>
            {children}
        </FormControl>
    );
};

/**
 * Basic text input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {boolean} autoFocus True to allow this input to capture focus.
 * @param {string} type The type of the input.
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} placeholder Field placeholder
 * @param {boolean} readOnly If input is read only
 *
 * @return {JSX.Element}
 */

export const InputInput = ({ label, required, disabled, name, value, autoFocus, type = "text", error = null, margin = "dense", onChange = () => null, description, placeholder, readOnly, uiProp }) => {
    const existingStyles = {
        "& legend": { display: "none" },
        "& fieldset": { top: 0 },
        ".MuiInputBase-root": { overflow: "hidden" },
        ".MuiInputBase-input": { padding: "0.7rem", fontSize: "0.875rem" },
    };

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper
            label={uiProp?.form?.input?.label === false ? null : label}
            required={required}
            disabled={disabled}
            margin={margin}
            error={error}
            styleFormControl={uiProp?.wrapper_props?.custom_sx}
            inputClasses={inputClasses}
        >
            <TextField
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                disabled={disabled}
                readOnly={readOnly}
                autoFocus={autoFocus}
                helperText={error ? error : description}
                error={error !== null}
                sx={{
                    ...existingStyles,
                    ...uiProp?.component_props?.custom_sx,
                }}
            />
        </InputWrapper>
    );
};

/**
 * Basic number input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} placeholder Field placeholder
 *
 * @return {JSX.Element}
 */

export const InputNumber = ({ label, required, disabled, error = null, name, value, margin = "dense", onChange = () => null, description, placeholder, autoFocus, uiProp }) => {
    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <TextField
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                disabled={disabled}
                helperText={error ? error : description}
                error={error !== null}
                autoFocus={autoFocus}
                type="number"
                inputProps={{ min: 0, step: "any" }}
                sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    ".MuiInputBase-input": { padding: "0.8rem", fontSize: "0.875rem" },
                }}
                onWheel={(e) => e.target.blur()}
                onFocus={(event) => {
                    event.target.select();
                }}
            />
        </InputWrapper>
    );
};

/**
 * Basic checkbox input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 *
 * @return {JSX.Element}
 */

export const InputCheckbox = ({
    label,
    required,
    disabled,
    name,
    value,
    error = null,
    margin = "dense",
    onChange = () => null,
    description,
    labelStyle,
    styleCheckbox,
    styleCheckBoxWrapp,
    uiProp,
    id,
}) => {
    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper required={required} disabled={disabled} margin={margin} error={error} styleFormControl={styleCheckBoxWrapp} inputClasses={inputClasses}>
            <FormControlLabel
                control={<Checkbox name={name} id={id} checked={value} onChange={onChange} disabled={disabled} sx={styleCheckbox} />}
                label={label}
                sx={{ ".MuiTypography-root": { fontSize: "14px" }, ...labelStyle }}
            />
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic radio input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 *
 * @return {JSX.Element}
 */

export const InputRadio = ({ label, required, disabled, name, value, error = null, margin = "dense", onChange = () => null, description, uiProp }) => {
    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <FormControlLabel sx={{ ".MuiTypography-root": { fontSize: "0.875rem" } }} control={<Radio name={name} checked={value} onChange={onChange} disabled={disabled} />} label={label} />
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic switch input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {boolean} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 *
 * @return {JSX.Element}
 */

export const InputSwitch = ({ label, required, disabled, name, value, error = null, margin = "dense", onChange = () => null, description, fullWidth = true, uiProp }) => {
    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper required={required} disabled={disabled} margin={margin} error={error} fullWidth={fullWidth} inputClasses={inputClasses}>
            <FormControlLabel
                sx={{ ".MuiTypography-root": { fontSize: "0.875rem" } }}
                control={<Switch name={name} value={value} checked={value} onChange={onChange} disabled={disabled} />}
                label={label}
            />
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic select input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} fillFromApi Path to get select options from
 * @param {boolean} usePropName If api call should use prop name at the end of the path
 * @param {array} options Select options if there is no api call
 * @param {string} queryString Additional queryString for api call
 *
 * @return {JSX.Element}
 */

export const InputSelect = ({
    label,
    required,
    disabled,
    error = null,
    name,
    value,
    margin = "dense",
    onChange = () => null,
    description,
    fillFromApi,
    usePropName,
    options,
    queryString = "",
    className,
    onDataReceived = () => null,
    styleFormControl,
    uiProp,
}) => {
    const authCtx = useContext(AuthContext);
    const { api } = authCtx;
    const [opt, setOpt] = useState(options);

    useEffect(() => {
        let isMounted = true;
        let path = usePropName ? `${fillFromApi}/${name}?${queryString}` : `${fillFromApi}?${queryString}`;
        const fillDdl = async () => {
            await api
                .get(path)
                .then((response) => {
                    if (isMounted) {
                        let res = response?.payload;
                        setOpt(res);
                        onDataReceived(res);
                    }
                })
                .catch((error) => {
                    console.warn(error);
                });
        };

        if (fillFromApi) {
            fillDdl();
        }

        return () => {
            isMounted = false;
        };
    }, [fillFromApi, queryString]);

    useEffect(() => {
        setOpt(opt?.length > 0 ? opt : options);
    }, [options, opt]);

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} styleFormControl={styleFormControl} inputClasses={inputClasses}>
            <Select
                className={className}
                name={name}
                value={(opt ?? []).length === 0 ? "" : value}
                onChange={onChange}
                disabled={disabled}
                sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    "& .MuiSelect-select": { padding: "0.7rem", fontSize: "0.875rem" },
                }}
            >
                {(opt ?? []).map((item) => (
                    <MenuItem sx={{ fontSize: "0.875rem" }} key={item.id} value={item?.id} selected={item.id === value} disabled={item?.disabled ?? false} props={item.props} valuename={item?.name}>
                        {item?.name}
                    </MenuItem>
                ))}
            </Select>
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic select input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} fillFromApi Path to get select options from
 * @param {boolean} usePropName If api call should use prop name at the end of the path
 * @param {array} options Select options if there is no api call
 * @param {string} queryString Additional queryString for api call
 *
 * @return {JSX.Element}
 */

export const AutocompleteInput = ({
    label,
    required,
    disabled,
    error = null,
    name,
    value,
    margin = "dense",
    onChange = () => {},
    description,
    fillFromApi,
    usePropName,
    options,
    queryString = "",
    uiProp,
}) => {
    const authCtx = useContext(AuthContext);
    const { api } = authCtx;
    const [opt, setOpt] = useState(options);
    const [myValue, setMyValue] = useState(null);
    // Opt-in (per-field, via uiProp) re-query as the user types, same mechanism
    // AutocompleteTagsFilled already uses - the initial load below only ever returns the DDL's
    // own first page (see e.g. Company::get_data()'s MAX_RESULTS), so for a large dataset
    // (thousands of rows) typing something past that first page finds nothing without this.
    // null (not "") so this never fires before the user has actually typed anything, avoiding a
    // redundant duplicate of the initial fetch on mount.
    const [searchTerm, setSearchTerm] = useState(null);
    const allowFillFromApiSearch = uiProp?.allowFillFromApiSearch ?? false;
    const searchParamName = uiProp?.searchParamName ?? "search";
    // While the user is actively typing (search-as-you-type), the options list keeps
    // refreshing (see the debounced effect below) - without this guard, the "sync
    // selected label from opt" effect fires on every refresh and blanks out what the
    // user just typed (using the stale, not-yet-selected `value`), closing the dropdown
    // right as they try to click a result.
    const userIsTypingRef = useRef(false);

    useEffect(() => {
        let isMounted = true;
        let path = usePropName ? `${fillFromApi}/${name}?${queryString}` : `${fillFromApi}?${queryString}`;
        const fillDdl = async () => {
            await api
                .get(path)
                .then((response) => {
                    if (isMounted) {
                        setOpt(response?.payload?.filter((item) => item !== null));
                    }
                })
                .catch((error) => {
                    console.warn(error);
                });
        };

        if (fillFromApi) {
            fillDdl();
        }

        return () => {
            isMounted = false;
        };
    }, [fillFromApi, queryString]);

    useEffect(() => {
        if (!allowFillFromApiSearch || !fillFromApi || searchTerm === null) return;

        let isMounted = true;
        const delay = setTimeout(() => {
            const basePath = usePropName ? `${fillFromApi}/${name}` : fillFromApi;
            const withQueryString = queryString ? appendQuery(basePath, queryString) : basePath;
            const path = appendQuery(withQueryString, `${searchParamName}=${encodeURIComponent(searchTerm)}`);

            api.get(path)
                .then((response) => {
                    if (isMounted) {
                        setOpt(response?.payload?.filter((item) => item !== null));
                    }
                })
                .catch((error) => console.warn(error));
        }, 300);

        return () => {
            isMounted = false;
            clearTimeout(delay);
        };
    }, [searchTerm, allowFillFromApiSearch, fillFromApi, name, usePropName, queryString, searchParamName]);

    // Backfill: an already-selected value may not be present in the DDL's capped/filtered
    // result set (e.g. a previously chosen company that isn't in the first page alphabetically)
    // - without this it would silently render as unselected even though a value is saved. Only
    // meaningful for the same DDLs that opted into allowFillFromApiSearch (a plain/short DDL
    // always returns everything, so this can never be needed there).
    useEffect(() => {
        if (!allowFillFromApiSearch || !fillFromApi || value === null || value === undefined || value === "") return;
        if (opt.some((o) => o.id === value)) return;

        let isMounted = true;
        const basePath = usePropName ? `${fillFromApi}/${name}` : fillFromApi;
        const withQueryString = queryString ? appendQuery(basePath, queryString) : basePath;
        const path = appendQuery(withQueryString, `ids=${encodeURIComponent(value)}`);

        api.get(path)
            .then((response) => {
                if (!isMounted) return;
                const backfilled = (response?.payload ?? []).filter((item) => item !== null);
                if (backfilled.length) {
                    setOpt((prev) => [...prev, ...backfilled.filter((b) => !prev.some((o) => o.id === b.id))]);
                }
            })
            .catch((error) => console.warn(error));

        return () => {
            isMounted = false;
        };
        // `opt` intentionally excluded: this only reacts to `value` changing - re-running it
        // whenever `opt` changes (e.g. right after this same effect updates it) would loop.
    }, [value, allowFillFromApiSearch, fillFromApi, name, usePropName, queryString]);

    useEffect(() => {
        if (userIsTypingRef.current) return;
        let selectedOption = null;
        if (opt.length > 0) {
            selectedOption = opt.find((o) => o.id === value)?.name;
            if (selectedOption === undefined) {
                selectedOption = null;
            }
            setMyValue(selectedOption);
        }
    }, [opt]);

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <Autocomplete
                value={myValue}
                onChange={(event, newValue) => {
                    userIsTypingRef.current = false;
                    setMyValue(newValue ?? null);
                    let selectedOption = opt.find((o) => o.name === newValue);
                    onChange(name, selectedOption ? selectedOption.id : newValue ?? "");
                }}
                onInputChange={(event, newInputValue, reason) => {
                    if (reason !== "input") {
                        userIsTypingRef.current = false;
                        return;
                    }
                    userIsTypingRef.current = true;
                    let newIval = newInputValue ? newInputValue : "";
                    setMyValue(newIval);
                    if (opt.length > 0) {
                        let selectedOption = opt.find((o) => o.name === newInputValue);
                        if (selectedOption) {
                            newIval = selectedOption.id;
                        }
                    }
                    onChange(name, newIval);
                    if (allowFillFromApiSearch) {
                        setSearchTerm(newInputValue ?? "");
                    }
                }}
                options={opt.map((option) => option?.name)}
                sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    ".MuiInputBase-root": { padding: "0.23rem !important" },
                }}
                renderInput={(params) => <TextField {...params} />}
            />
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

export const AutocompleteInputFreeSolo = ({
    label,
    required,
    disabled,
    error = null,
    name,
    value,
    margin = "dense",
    onChange = () => {},
    description,
    fillFromApi,
    usePropName,
    options,
    queryString = "",
    uiProp,
}) => {
    const authCtx = useContext(AuthContext);
    const { api } = authCtx;
    const [opt, setOpt] = useState([]);
    const filter = createFilterOptions();

    useEffect(() => {
        let isMounted = true;
        let path = usePropName ? `${fillFromApi}/${name}?${queryString}` : `${fillFromApi}?${queryString}`;
        const fillDdl = async () => {
            await api
                .get(path, false)
                .then((response) => {
                    if (isMounted) {
                        const { payload } = response;
                        setOpt(payload);
                    }
                })
                .catch((error) => {
                    console.warn(error);
                });
        };

        if (fillFromApi) {
            fillDdl();
        }

        return () => {
            isMounted = false;
        };
    }, [fillFromApi, queryString]);

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <Autocomplete
                disabled={disabled}
                value={value}
                onChange={(event, newInputValue) => {
                    let newIval = newInputValue ? newInputValue : "";
                    if (opt.length > 0) {
                        let selectedOption = opt.find((o) => o.name === newInputValue);
                        if (selectedOption) {
                            newIval = selectedOption.id;
                        }
                    }
                    onChange(name, newIval);
                }}
                options={options?.length > 0 ? options : opt}
                renderOption={(props, option) => <li {...props}>{option.name}</li>}
                filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some((option) => inputValue === option.name);
                    if (inputValue !== "" && !isExisting) {
                        filtered.push({
                            inputValue,
                            name: `Dodaj: "${inputValue}"`,
                        });
                    }
                    return filtered;
                }}
                getOptionLabel={(option) => {
                    // Value selected with enter, right from the input
                    if (typeof option === "string") {
                        return option;
                    }
                    // Add "xxx" option created dynamically
                    if (option.inputValue) {
                        return option.inputValue;
                    }
                    // Regular option
                    return option.name;
                }}
                sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    ".MuiInputBase-root": { padding: "0 !important" },
                    ".MuiOutlinedInput-root .MuiAutocomplete-input": { padding: "0.7rem !important", fontSize: "0.875rem" },
                }}
                renderInput={(params) => <TextField {...params} />}
                freeSolo={true}
            />
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic select input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} fillFromApi Path to get select options from
 * @param {boolean} usePropName If api call should use prop name at the end of the path
 * @param {array} options Select options if there is no api call
 * @param {string} queryString Additional queryString for api call
 *
 * @return {JSX.Element}
 */
// Component that allows you to select more values, as well as add new values
// Appends a query param string to a base URL that may already carry its own query string
// (e.g. fillFromApi = "admin/export/filters/values/b2c_product_item?field_id=..."), so a
// second "?" never gets appended and silently corrupts whatever came before it.
const appendQuery = (baseUrl, extra) =>
    `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}${extra}`;

export const AutocompleteTagsFilled = ({
    label,
    required,
    disabled,
    error = null,
    name,
    value,
    margin = "dense",
    onChange = () => {},
    description,
    fillFromApi,      // e.g. "admin/news-b2c/news/basic-data/ddl"
    usePropName,      // usually true → makes /ddl/tag
    options,
    queryString = "",
    uiProp,
}) => {
    const { api } = useContext(AuthContext);

    const [opt, setOpt] = useState(options || []);
    const [myValue, setMyValue] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const filter = createFilterOptions();
    const allowFreeSolo = uiProp?.allowFreeSolo ?? true;
    const allowFillFromApiSearch = uiProp?.allowFillFromApiSearch ?? false;
    
    // --- 1️⃣ Load all existing tags initially ---
    useEffect(() => {
        if (!fillFromApi) return;
        let isMounted = true;

        // always call /ddl/tag for initial load
        const path = usePropName
            ? appendQuery(`${fillFromApi}/${name}`, queryString) // → /ddl/tag
            : appendQuery(fillFromApi, queryString);

        api.get(path)
            .then((response) => {
                if (!isMounted) return;
                const data = response?.payload ?? [];

                // Keep whichever options are currently selected even if this page doesn't
                // include them (e.g. a selected value ranked past the endpoint's own top-N),
                // otherwise this races effect 3️⃣'s backfill fetch: whichever resolves last
                // wins, and replacing opt wholesale here can wipe out ids 3️⃣ already merged
                // in - the chips would flash in then disappear depending on fetch timing.
                const selectedIds = Array.isArray(value) ? value : (value?.exist ?? []);
                setOpt((prev) => {
                    const keepSelected = (prev ?? []).filter((o) => selectedIds.includes(o.id) && !data.some((d) => d.id === o.id));
                    return [...keepSelected, ...data];
                });
            })
            .catch(console.warn);

        return () => {
            isMounted = false;
        };
    }, [fillFromApi, name, queryString, usePropName, api]);

    // --- 2️⃣ Map backend value (IDs or object) into display names ---
    useEffect(() => {
        if (!opt?.length || !value) return;

        const selectedOptions = [];

        // handle { exist: [], new: [] }
        if (typeof value === "object" && Array.isArray(value.exist)) {
            value.exist.forEach((id) => {
                const found = opt.find((o) => o.id === id);
                if (found) selectedOptions.push(found.name);
            });
            if (Array.isArray(value.new)) {
                selectedOptions.push(...value.new);
            }
        }
        // handle [1,6,7]
        else if (Array.isArray(value)) {
            value.forEach((id) => {
                const found = opt.find((o) => o.id === id);
                if (found) selectedOptions.push(found.name);
            });
        }

        setMyValue(selectedOptions);
    }, [opt, value]);

    // --- 3️⃣ If some IDs returned from API aren’t yet in opt, refetch them from SAME endpoint /ddl/tag?ids=... ---
    useEffect(() => {
        // Normalize both the plain-array shape ([1,6,7]) and the "one of / not one of" shape
        // ({exist:[...], new:[...]}) - previously this only handled the plain array, so a
        // selected value not already in the initial page of opt (e.g. an alphabetically-late
        // SKU past the picker's own top-50) never got backfilled and silently showed no chip.
        const selectedIds = Array.isArray(value) ? value : (value?.exist ?? []);
        if (!selectedIds.length || !fillFromApi) return;

        // find IDs missing in current opt
        const missingIds = selectedIds.filter((id) => !opt.some((o) => o.id === id));
        if (!missingIds.length) return;

        // Batch requests so the "ids=" query string can't grow large enough to hit a
        // URL-length limit (browser/proxy/webserver) - a picker with hundreds of
        // selected SKUs was silently failing to backfill because the whole list was
        // sent as one giant GET query string.
        const IDS_CHUNK_SIZE = 100;
        const chunks = [];
        for (let i = 0; i < missingIds.length; i += IDS_CHUNK_SIZE) {
            chunks.push(missingIds.slice(i, i + IDS_CHUNK_SIZE));
        }

        const fetchMissing = async () => {
            const basePath = usePropName
                ? `${fillFromApi}/${name}`
                : `${fillFromApi}`;

            const results = await Promise.all(
                chunks.map((chunk) => {
                    const url = appendQuery(basePath, `ids=${chunk.join(",")}`);
                    return api.get(url).catch((err) => {
                        console.warn("Failed to load missing tags:", err);
                        return null;
                    });
                })
            );

            const newData = results
                .filter(Boolean)
                .flatMap((resp) => resp?.payload ?? []);
            if (!newData.length) return;

            // merge missing tags
            setOpt((prev) => [
                ...prev,
                ...newData.filter((n) => !prev.some((p) => p.id === n.id)),
            ]);
        };

        fetchMissing();
    }, [value, fillFromApi, name, usePropName]);

    useEffect(() => {
        if (!fillFromApi) return;
        if (!inputValue) return;
        if (!allowFillFromApiSearch) return;

        const delay = setTimeout(() => {
            const path = usePropName
                ? appendQuery(`${fillFromApi}/${name}`, `search_term=${encodeURIComponent(inputValue)}`)
                : appendQuery(fillFromApi, `search_term=${encodeURIComponent(inputValue)}`);

            api.get(path)
                .then((res) => {
                    const newData = res?.payload ?? [];

                    // Keep whichever options are currently selected even if the new search
                    // doesn't match them, otherwise their chip disappears (effect 2️⃣ re-derives
                    // myValue from opt, and a selected id missing from opt gets silently dropped).
                    const selectedIds = Array.isArray(value) ? value : (value?.exist ?? []);

                    setOpt((prev) => {
                        const keepSelected = prev.filter((o) => selectedIds.includes(o.id));
                        const merged = [...keepSelected, ...newData];
                        const seen = new Set();
                        return merged.filter((o) => {
                            if (seen.has(o.id)) return false;
                            seen.add(o.id);
                            return true;
                        });
                    });
                })
                .catch(console.warn);
        }, 300); // debounce

        return () => clearTimeout(delay);
    }, [inputValue]);

    // --- 4️⃣ Handle user selecting or creating new tags ---
    const handleChange = (event, newValue) => {
        setInputValue("");

        const sanitizedValue = allowFreeSolo
            ? newValue
            : newValue.filter((item) =>
                opt.some((o) => o.name === item)
            );
            
        setMyValue(sanitizedValue);

        const for_save = { exist: [], new: [] };

        sanitizedValue.forEach((item) => {
            const found = opt.find((o) => o.name === item);
            if (found) for_save.exist.push(found.id);
            else for_save.new.push(item);
        });

        onChange(name, for_save);
    };

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper
            label={label}
            required={required}
            disabled={disabled}
            margin={margin}
            error={error}
            inputClasses={inputClasses}
        >
            <Autocomplete
                multiple
                freeSolo={allowFreeSolo}
                filterOptions={(optionsList, params) => {
                    const filtered = filter(optionsList, params);
                    if (!allowFreeSolo) {
                        return filtered;
                    }
                    const { inputValue } = params;
                    const exists = optionsList.some((optItem) => inputValue === optItem);
                    if (inputValue !== "" && !exists) {
                        filtered.push(inputValue);
                    }
                    return filtered;
                }}
                value={myValue}
                inputValue={inputValue}
                onInputChange={(e, v) => setInputValue(v)}
                onChange={handleChange}
                options={opt.map((option) => option.name)}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            key={index}
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                        />
                    ))
                }
                renderInput={(params) => <TextField {...params} label={label} />}
                sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    ".MuiFormLabel-root": { display: "none" },
                    ".MuiInputBase-input": { padding: "0.7rem", fontSize: "0.875rem" },
                }}
            />
            <FormHelperText>{error || description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic select input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} fillFromApi Path to get select options from
 * @param {boolean} usePropName If api call should use prop name at the end of the path
 * @param {array} options Select options if there is no api call
 * @param {string} queryString Additional queryString for api call
 * @param {boolean} selectAllEnabled Determines if select and deselect all buttons are displayed or not
 *
 * @return {JSX.Element}
 */

export const InputMultiSelect = ({
    label,
    required,
    disabled,
    error = null,
    name,
    value,
    margin = "dense",
    onChange = () => null,
    description,
    fillFromApi,
    usePropName,
    options,
    queryString = "",
    styleMultiSelect,
    uiProp,
    selectAllEnabled = false,
}) => {
    const authCtx = useContext(AuthContext);
    const { api } = authCtx;
    const [opt, setOpt] = useState(options);
    useEffect(() => {
        let isMounted = true;
        let path = usePropName ? `${fillFromApi}/${name}?${queryString}` : `${fillFromApi}?${queryString}`;
        const fillDdl = async () => {
            await api
                .get(path)
                .then((response) => {
                    if (isMounted) {
                        setOpt(response?.payload);
                    }
                })
                .catch((error) => {
                    console.warn(error);
                });
        };

        if (fillFromApi) {
            fillDdl();
        }

        return () => {
            isMounted = false;
        };
    }, [fillFromApi]);

    useEffect(() => {
        if (options && options?.length > 0) {
            setOpt(options);
        } else {
            setOpt(opt);
        }
    }, [options, opt]);

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} styleFormControl={styleMultiSelect} inputClasses={inputClasses}>
            <Select
                name={name}
                value={(opt ?? []).length === 0 ? [] : value}
                onChange={onChange}
                disabled={disabled}
                multiple={true}
                renderValue={(selected) => {
                    let display = [];
                    for (const option of opt) {
                        if (selected.includes(option.id)) {
                            display.push(option.name);
                        }
                    }
                    return display.join(", ");
                }}
                sx={{
                    position: "relative",
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    "& .MuiSelect-select": { padding: "0.7rem", fontSize: "0.875rem" },
                }}
            >
                {selectAllEnabled && (
                    <Box
                        sx={{ display: "flex", gap: "0.7rem", padding: "0.7rem", position: "sticky", top: 0, background: "white", opacity: 1, zIndex: 20 }}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        <Button
                            label="Izaberi sve"
                            variant="contained"
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange({ target: { value: opt.map((item) => item.id), name } });
                            }}
                        />
                        <Button
                            label="Poništi sve"
                            variant="contained"
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange({ target: { value: [], name } });
                            }}
                        />
                    </Box>
                )}
                {(opt ?? []).map((item) => (
                    <MenuItem sx={{ fontSize: "0.875rem" }} key={item.id} value={item.id} selected={item.id === value} disabled={item?.disabled ?? false}>
                        <ListItemIcon>
                            <Checkbox checked={value?.indexOf(item.id) > -1} />
                        </ListItemIcon>

                        {item.name}
                    </MenuItem>
                ))}
            </Select>
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic textarea input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} placeholder Field placeholder
 *
 * @return {JSX.Element}
 */

export const InputText = ({ label, required, disabled, error = null, name, value, margin = "dense", onChange = () => null, description, placeholder, uiProp }) => {
    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <TextField
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                disabled={disabled}
                multiline
                minRows={3}
                helperText={error ? error : description}
                error={error !== null}
                sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    ".MuiInputBase-root": { padding: "0.7rem" },
                    ".MuiInputBase-input": { fontSize: "0.875rem" },
                    ".MuiFormHelperText-root": { whiteSpace: "pre-line", wordBreak: "break-word" },
                }}
            />
        </InputWrapper>
    );
};

/**
 * Basic datetime input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 *
 * @return {JSX.Element}
 */

export const InputDateTime = ({ label, required, disabled, error = null, name, value, margin = "dense", onChange = () => null, description, uiProp }) => {
    const handleChange = (newValue) => {
        const ev = {
            target: {
                name: name,
                value: newValue,
            },
        };
        onChange(ev, "date_time");
    };

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    let minDateTime = uiProp?.minDateTime ?? null;
    if(minDateTime == 'now()') {
        minDateTime = new Date();
    }

    console.log({
        "action": "InputDateTime",
        "minDateTime": minDateTime
    });

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                    value={value !== "" ? value : null}
                    onChange={handleChange}
                    ampm={false}
                    showToolbar
                    disabled={disabled}
                    inputFormat="dd/MM/yyyy HH:mm"
                    minDateTime={minDateTime}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            sx={{
                                "& legend": { display: "none" },
                                "& fieldset": { top: 0 },
                                ".MuiInputBase-input": { fontSize: "0.875rem", padding: "0.7rem" },
                            }}
                        />
                    )}
                />
            </LocalizationProvider>
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Basic date input
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 *
 * @return {JSX.Element}
 */

export const InputDate = ({ label, required, disabled, error = null, name, value, margin = "dense", onChange = () => null, description, uiProp }) => {
    const handleChange = (newValue) => {
        const ev = {
            target: {
                name: name,
                value: newValue,
            },
        };
        onChange(ev, "date");
    };

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    value={value !== "" ? value : null}
                    onChange={handleChange}
                    ampm={false}
                    showToolbar
                    disabled={disabled}
                    inputFormat="dd/MM/yyyy"
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            sx={{
                                "& legend": { display: "none" },
                                "& fieldset": { top: 0 },
                                ".MuiInputBase-input": { fontSize: "0.875rem", padding: "0.7rem" },
                            }}
                        />
                    )}
                />
            </LocalizationProvider>
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

/**
 * Input that returns html
 *
 * @param {string} label Field label
 * @param {boolean} required If field is required
 * @param {boolean} disabled If field is disabled
 * @param {string} error Error message
 * @param {string} name Input field name
 * @param {string} value Field value
 * @param {"none"|"dense"|"normal"} margin The margin to use for FormControl.
 * @param {function} onChange Change handler for the field
 * @param {string} description Field description
 * @param {string} placeholder Field placeholder
 *
 * @return {JSX.Element}
 */

export const InputHtml = ({ label, required, disabled, name, value, error = null, margin = "dense", onChange = () => null, description, uiProp }) => {
    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <HtmlEditor name={name} value={value} onChange={onChange} />
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

export const ImportPicker = ({ label, required, disabled, margin, error = null, onFilePicked, description, selectedFile, uiProp }) => {
    const ref = useRef();

    const handleChange = (event) => {
        const files = Array.from(event.target.files);
        const [file] = files;

        const fileExtension = file.name.split(".").pop().toLowerCase();

        if (fileExtension !== "csv" && fileExtension !== "xml" && fileExtension !== "json") {
            toast.error("Pogrešan tip fajla.");
            return;
        }

        blobToData(file).then((result) => {
            let obj = {
                base_64: result,
                name: file?.name,
            };
            onFilePicked(obj);
        });
    };

    const inputClasses = generateBootstrapClasses(uiProp?.columns);

    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <ButtonBase
                component="label"
                sx={{
                    height: "2.844rem",
                    border: "1px solid",
                    borderColor: error ? "#d32f2f" : "rgba(0, 0, 0, 0.23)",
                    borderRadius: "0.25rem",
                    justifyContent: "start",
                    paddingLeft: "0.875rem",
                }}
            >
                {selectedFile ? (
                    <span style={{ fontSize: "0.875rem", WebkitTextFillColor: disabled ? "rgba(0, 0, 0, 0.38)" : "initial" }}>Izabrani fajl: {selectedFile.name}</span>
                ) : (
                    <span style={{ fontSize: "0.875rem", WebkitTextFillColor: disabled ? "rgba(0, 0, 0, 0.38)" : "initial" }}>Kliknite ovde kako biste odabrali fajl za import.</span>
                )}
                <Input type="file" onChange={handleChange} inputRef={ref} disabled={disabled} sx={{ display: "none" }} error={error !== null} />
            </ButtonBase>
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};

export const FilePicker = ({
    label,
    required,
    disabled,
    margin,
    value,
    error = null,
    onFilePicked = () => {},
    description,
    selectedFile,
    uiProp,
    multipleFileSelection = false,
    handleRemoveFile = () => {},
    onChange,
    name,
}) => {
    const ref = useRef();
    const [filename, setFilename] = useState("");
    const handleChange = (event) => {
        const files = Array.from(event.target.files);
        const [file] = files;

        blobToData(file);

        const reader = new FileReader();
        reader.onload = (e) => {
            const base64 = e.target.result;
            const obj = {
                base_64: base64,
                file_name: file?.name,
                file: file,
                name,
            };

            setFilename(file.name);
            onFilePicked(obj);
            onChange({ target: obj }, "file_picker");
        };
        reader.readAsDataURL(file);
    };

    const getFileTypeLabel = (value) => {
        if (!value.startsWith("data:")) return value;

        const mimeType = value.split(";")[0].split(":")[1];

        if (!mimeType) return "Base64 fajl";

        if (mimeType.startsWith("video/")) return "Base64 video";
        if (mimeType.startsWith("image/")) return "Base64 slika";
        if (mimeType.startsWith("audio/")) return "Base64 audio";
        if (mimeType === "application/pdf") return "Base64 PDF";
        if (mimeType === "application/json") return "Base64 JSON";

        return "Base64 fajl";
    };

    const renderSelectedFiles = () => {
        if (multipleFileSelection) {
            return (
                selectedFile && (
                    <span style={{ fontSize: "0.9rem", WebkitTextFillColor: disabled ? "rgba(0, 0, 0, 0.38)" : "initial", display: "flex", width: "100%" }}>
                        Izabrani fajlovi:
                        {selectedFile.length > 0
                            ? selectedFile.map((file, index) => {
                                  return (
                                      <Box
                                          key={file.name}
                                          sx={{
                                              marginLeft: index === 0 ? "0.3rem" : "0",
                                              whiteSpace: "wrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                          }}
                                      >
                                          {index > 0 && ", "}
                                          {file.name}
                                      </Box>
                                  );
                              })
                            : " Kliknite ovde kako biste odabrali fajl."}
                    </span>
                )
            );
        } else {
            return filename || value ? (
                <span style={{ fontSize: "0.9rem", WebkitTextFillColor: disabled ? "rgba(0, 0, 0, 0.38)" : "initial" }}>Izabrani fajl: {filename || getFileTypeLabel(value)}</span>
            ) : (
                <span style={{ fontSize: "0.9rem", WebkitTextFillColor: disabled ? "rgba(0, 0, 0, 0.38)" : "initial" }}>Kliknite ovde kako biste odabrali fajl.</span>
            );
        }
    };

    const renderRemoveFilesList = () => {
        if (multipleFileSelection) {
            return selectedFile?.map((item) => (
                <span
                    span
                    key={item?.name}
                    style={{ fontSize: "0.9rem", WebkitTextFillColor: disabled ? "rgba(0, 0, 0, 0.38)" : "initial", display: "flex", alignItems: "center", marginRight: "0.8rem" }}
                >
                    {item?.name}
                    <CloseIcon
                        sx={{ fontSize: "0.9rem", cursor: "pointer", marginLeft: "0.2rem" }}
                        onClick={() => {
                            handleRemoveFile(item);
                        }}
                    />
                </span>
            ));
        }
    };

    const inputClasses = generateBootstrapClasses(uiProp?.columns);
    return (
        <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} inputClasses={inputClasses}>
            <ButtonBase
                component="label"
                sx={{
                    height: "2.844rem",
                    border: "1px solid",
                    borderColor: error ? "#d32f2f" : "rgba(0, 0, 0, 0.23)",
                    borderRadius: "0.25rem",
                    justifyContent: "start",
                    paddingLeft: "0.875rem",
                }}
            >
                <Input
                    type="file"
                    onChange={handleChange}
                    inputRef={ref}
                    disabled={disabled}
                    error={error !== null}
                    sx={{ display: "none" }}
                    multiple
                    inputProps={{
                        accept: uiProp?.fileUpload?.allow_format?.map((format) => format?.mime_type),
                    }}
                />
                {renderSelectedFiles()}
            </ButtonBase>
            <Box sx={{ display: "flex" }}>{renderRemoveFilesList()}</Box>
            <FormHelperText>{error ? error : description}</FormHelperText>
        </InputWrapper>
    );
};
